import { useState, useEffect, useRef, useMemo } from 'react';
import { isEmpty, round2, focusWidget, newUid } from '@/util';
import { FroalaTextareaView } from '@/components/Froala/FroalaTextareaView';
import { WidgetHeader } from './WidgetHeader';
import { useTranslation } from 'react-i18next';
import { encode } from '@/base64';
import { ExplanationSection } from '../ExplanationSection';
import { PartialSubmitSection } from '../PartialSubmitSection';
import { TrashIcon } from '@teo/components/icons';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const pointInLabel = (marker, label) => {
  return (
    marker.x >= label.x &&
    marker.x <= label.x + label.w &&
    marker.y >= label.y &&
    marker.y <= label.y + label.h
  );
};

const buildCorrection = (state, answer) => {
  const labels = state?.labels || [];
  const markers = answer?.markers || [];
  const orderedMarkers = !!state?.orderedMarkers;

  const labelResults = {};
  const markerResults = {};
  let rawUnitsScore = 0;
  let matchedCount = 0;

  const labelsWithAnyMarker = new Set();

  markers.forEach((marker) => {
    labels.forEach((label) => {
      if (pointInLabel(marker, label)) {
        labelsWithAnyMarker.add(label.uid);
      }
    });
  });

  if (orderedMarkers) {
    labels.forEach((label, index) => {
      const marker = markers[index];

      if (marker && pointInLabel(marker, label)) {
        matchedCount += 1;
        rawUnitsScore += 1;

        labelResults[label.uid] = {
          uid: label.uid,
          matched: true,
          markerUid: marker.uid,
          hasAnyMarker: true,
        };

        markerResults[marker.uid] = {
          uid: marker.uid,
          isCorrect: true,
          isIgnored: false,
          labelUid: label.uid,
          displayIndex: index + 1,
        };
      } else {
        labelResults[label.uid] = {
          uid: label.uid,
          matched: false,
          markerUid: marker?.uid || null,
          hasAnyMarker: false,
        };

        if (marker) {
          rawUnitsScore -= 1;

          const realLabelIndex = labels.findIndex((x) =>
            pointInLabel(marker, x)
          );

          const realLabel = realLabelIndex >= 0 ? labels[realLabelIndex] : null;

          if (realLabel) {
            labelResults[realLabel.uid] = {
              ...(labelResults[realLabel.uid] || {
                uid: realLabel.uid,
                matched: false,
                markerUid: null,
              }),
              hasAnyMarker: true,
            };
          }

          markerResults[marker.uid] = {
            uid: marker.uid,
            isCorrect: false,
            isIgnored: false,
            labelUid: realLabel?.uid || null,
            displayIndex: realLabelIndex >= 0 ? realLabelIndex + 1 : index + 1,
          };
        }
      }
    });

    markers.slice(labels.length).forEach((marker, index) => {
      rawUnitsScore -= 1;

      const realLabelIndex = labels.findIndex((x) => pointInLabel(marker, x));
      const realLabel = realLabelIndex >= 0 ? labels[realLabelIndex] : null;

      if (realLabel) {
        labelResults[realLabel.uid] = {
          ...(labelResults[realLabel.uid] || {
            uid: realLabel.uid,
            matched: false,
            markerUid: null,
          }),
          hasAnyMarker: true,
        };
      }

      markerResults[marker.uid] = {
        uid: marker.uid,
        isCorrect: false,
        isIgnored: false,
        labelUid: realLabel?.uid || null,
        displayIndex:
          realLabelIndex >= 0 ? realLabelIndex + 1 : labels.length + index + 1,
      };
    });
  } else {
    labels.forEach((label) => {
      const markersInside = markers.filter((marker) =>
        pointInLabel(marker, label)
      );

      if (markersInside.length > 0) {
        const firstMarker = markersInside[0];

        matchedCount += 1;
        rawUnitsScore += 1;

        labelResults[label.uid] = {
          uid: label.uid,
          matched: true,
          markerUid: firstMarker.uid,
        };

        markerResults[firstMarker.uid] = {
          uid: firstMarker.uid,
          isCorrect: true,
          isIgnored: false,
          labelUid: label.uid,
        };

        markersInside.slice(1).forEach((marker) => {
          markerResults[marker.uid] = {
            uid: marker.uid,
            isCorrect: false,
            isIgnored: true,
            labelUid: label.uid,
          };
        });
      } else {
        labelResults[label.uid] = {
          uid: label.uid,
          matched: false,
          markerUid: null,
        };
      }
    });

    markers.forEach((marker) => {
      if (!markerResults[marker.uid]) {
        rawUnitsScore -= 1;

        markerResults[marker.uid] = {
          uid: marker.uid,
          isCorrect: false,
          isIgnored: false,
          labelUid: null,
        };
      }
    });
  }

  rawUnitsScore = Math.max(0, rawUnitsScore);

  const total = Math.max(labels.length, 1);

  let finalScore = 0;

  if (!state?.excludeScore) {
    finalScore = (rawUnitsScore / total) * (state.score || 0);
  }

  if (state?.strictCorrection && matchedCount !== labels.length) {
    finalScore = 0;
  }

  return {
    uid: state.uid,
    score: finalScore,
    matchedCount,
    total: labels.length,
    labels: labels.map((label) => ({
      uid: label.uid,
      matched: !!labelResults[label.uid]?.matched,
      markerUid: labelResults[label.uid]?.markerUid || null,
      hasAnyMarker: labelsWithAnyMarker.has(label.uid),
    })),
    markers: markers.map((marker) => ({
      uid: marker.uid,
      x: marker.x,
      y: marker.y,
      isCorrect: !!markerResults[marker.uid]?.isCorrect,
      isIgnored: !!markerResults[marker.uid]?.isIgnored,
      labelUid: markerResults[marker.uid]?.labelUid || null,
      displayIndex: markerResults[marker.uid]?.displayIndex,
    })),
  };
};

export const ShowOnPictureView = ({
  state,
  answer,
  correction,
  correctionType = undefined,
  courseStatus = undefined,
  index,
  isCorrected,
  viewOnly,
  setAnswerFn = undefined,
  setXApiSubmitFn = undefined,
  setCorrectionFn = undefined,
  onModified = undefined,
  onSave = undefined,
  resultPages,
  showAnswers = false,
}) => {
  const { t } = useTranslation();
  const safeAnswer = useMemo(() => {
    const next = answer ? { ...answer } : {};
    if (!Array.isArray(next.markers)) next.markers = [];
    if (typeof next.submitted !== 'boolean') next.submitted = false;
    return next;
  }, [answer]);

  const safeCorrection = useMemo(() => {
    return correction ? { ...correction } : {};
  }, [correction]);

  const [submitted, setSubmitted] = useState(!!safeAnswer?.submitted);
  const answered = submitted || isCorrected;

  const [markers, setMarkers] = useState(
    Array.isArray(safeAnswer?.markers) ? safeAnswer.markers : []
  );
  const [score, setScore] = useState(
    safeCorrection?.score !== undefined ? safeCorrection.score : 0
  );
  const [modified, setModified] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [draggingUid, setDraggingUid] = useState(null);
  const [dragOverTrash, setDragOverTrash] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);

  const imageWrapRef = useRef(null);
  const trashRef = useRef(null);
  const dragStateRef = useRef(null);
  const suppressAddRef = useRef(false);
  const dragMovedRef = useRef(false);

  const currentAnswer = useMemo(() => {
    return {
      ...safeAnswer,
      uid: state.uid,
      markers,
      submitted,
      incomplete: !submitted && markers.length < (state?.labels?.length || 0),
    };
  }, [safeAnswer, state.uid, state?.labels?.length, markers, submitted]);

  answer ||= {};
  correction ||= {};

  ShowOnPictureView.syncStates(state, answer, correction);

  const currentCorrection = useMemo(() => {
    return buildCorrection(state, currentAnswer);
  }, [state, currentAnswer]);

  useEffect(() => {
    if (submitted) {
      let answer = getAnswer();
      let correction = {};
      ShowOnPictureView.syncStates(state, answer, correction);
      if (!answer?.answered) {
        setScore(correction?.score);
      }
    }
  }, [submitted]);

  const getAnswer = () => {
    answer.markers = markers;
    answer.incomplete = !submitted && markers?.length < 1;
    answer.submitted = submitted;
    return answer;
  };
  setAnswerFn && setAnswerFn(getAnswer);

  const getCorrection = () => {
    let correction = {};
    ShowOnPictureView.syncStates(state, getAnswer(), correction);
    return correction;
  };
  setCorrectionFn && setCorrectionFn(getCorrection);

  useEffect(() => {
    modified && onModified && onModified();
  }, [onModified, modified]);

  const getImageRect = () => {
    return imageWrapRef.current?.getBoundingClientRect() || null;
  };

  const addMarker = (clientX, clientY) => {
    if (answered || viewOnly || !imageLoaded) return;

    const rect = getImageRect();
    if (!rect) return;

    const x = clamp((clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((clientY - rect.top) / rect.height, 0, 1);

    const nextMarkers = [
      ...markers,
      {
        uid: newUid(20),
        x,
        y,
      },
    ];

    setMarkers(nextMarkers);
    setModified(String(Date.now()));
  };

  const updateMarker = (uid, nextPos) => {
    setMarkers((prev) =>
      prev.map((marker) =>
        marker.uid === uid
          ? {
              ...marker,
              x: clamp(nextPos.x, 0, 1),
              y: clamp(nextPos.y, 0, 1),
            }
          : marker
      )
    );
  };

  const removeMarker = (uid) => {
    setMarkers((prev) => prev.filter((marker) => marker.uid !== uid));
    setModified(String(Date.now()));
  };

  const handleImagePointerUp = (e) => {
    if (answered || viewOnly) return;

    if (suppressAddRef.current || dragMovedRef.current) {
      suppressAddRef.current = false;
      dragMovedRef.current = false;
      return;
    }

    addMarker(e.clientX, e.clientY);
  };

  const startMarkerDrag = (e, markerUid) => {
    if (answered || viewOnly) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = getImageRect();
    if (!rect) return;

    suppressAddRef.current = true;
    dragMovedRef.current = false;

    setDraggingUid(markerUid);
    setDragPreview({
      x: e.clientX,
      y: e.clientY,
      uid: markerUid,
    });

    dragStateRef.current = {
      markerUid,
      rect,
      startX: e.clientX,
      startY: e.clientY,
    };

    if (e.currentTarget?.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        //
      }
    }
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!dragStateRef.current || answered || viewOnly) return;

      const { markerUid, startX, startY } = dragStateRef.current;

      if (
        Math.abs(e.clientX - startX) > 4 ||
        Math.abs(e.clientY - startY) > 4
      ) {
        dragMovedRef.current = true;
      }

      setDragPreview({
        x: e.clientX,
        y: e.clientY,
        uid: markerUid,
      });

      const trashRect = trashRef.current?.getBoundingClientRect();
      if (trashRect) {
        const overTrash =
          e.clientX >= trashRect.left &&
          e.clientX <= trashRect.right &&
          e.clientY >= trashRect.top &&
          e.clientY <= trashRect.bottom;

        setDragOverTrash(overTrash);
      }
    };

    const handleUp = (e) => {
      if (!dragStateRef.current) return;

      const { markerUid, rect } = dragStateRef.current;

      if (dragMovedRef.current) {
        if (dragOverTrash) {
          removeMarker(markerUid);
        } else {
          const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
          const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);

          updateMarker(markerUid, { x, y });
          setModified(String(Date.now()));
        }
      }

      dragStateRef.current = null;
      setDraggingUid(null);
      setDragOverTrash(false);
      setDragPreview(null);

      setTimeout(() => {
        suppressAddRef.current = false;
        dragMovedRef.current = false;
      }, 0);
    };

    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [dragOverTrash, answered, viewOnly]);

  const markerResultMap = useMemo(() => {
    const map = {};
    currentCorrection.markers.forEach((marker) => {
      map[marker.uid] = marker;
    });
    return map;
  }, [currentCorrection.markers]);

  const missedLabelMap = useMemo(() => {
    const map = {};

    currentCorrection.labels.forEach((label) => {
      if (!label.matched && !label.hasAnyMarker) {
        map[label.uid] = true;
      }
    });

    return map;
  }, [currentCorrection.labels]);

  return (
    <div
      className={`flex flex-col gap-4 py-4 ${
        !resultPages ? 'rounded-lg bg-[#f8f8f9] px-4' : 'mt-2'
      }`}
      data-state={encode(JSON.stringify(state))}
      onFocus={() => focusWidget(state?.uid)}
    >
      {!resultPages && (
        <WidgetHeader
          index={index}
          score={round2(score)}
          maxScore={state.score}
          answered={answered}
          titleWidget={state.titleWidget || t('widgets.type.show_on_picture')}
        />
      )}

      <FroalaTextareaView value={state.vraag} className="mb-2" />

      <div>
        {!answered && !viewOnly && imageLoaded && (
          <div
            ref={trashRef}
            className={`mb-4 flex min-h-[44px] items-center gap-3 rounded-md border px-4 transition ${
              dragOverTrash ? 'border-solid' : ' border-dashed'
            }`}
          >
            <TrashIcon
              className={`w-5 ${
                dragOverTrash ? '!text-error-04' : '!text-grey-04'
              } !text-error-04`}
            />
            <p className="text-xs font-medium text-grey-05">
              {t('widgets.show_onpicture.drag_here_to_remove')}
            </p>
          </div>
        )}
        <div className="relative mb-[30px]" id={state.uid + '_view'}>
          <div
            ref={imageWrapRef}
            className="relative touch-none overflow-hidden rounded-md"
            onClick={handleImagePointerUp}
          >
            <div className="pointer-events-none">
              <img
                id={state.uid + '_img'}
                src={state.image}
                onLoad={() => setImageLoaded(true)}
                className="block w-full select-none"
                style={{
                  cursor: answered || viewOnly ? 'default' : 'crosshair',
                }}
                draggable="false"
                alt=""
              />
            </div>

            {imageLoaded &&
              answered &&
              state?.labels?.map((label, index) => {
                if (!missedLabelMap[label.uid]) return null;

                return (
                  <div
                    key={label.uid}
                    className="pointer-events-none absolute flex items-center justify-center rounded-[4px] border-2 border-dashed border-error-04 bg-error-01/60 text-lg font-bold text-error-05"
                    style={{
                      left: `${(label.x + label.w / 2) * 100}%`,
                      top: `${(label.y + label.h / 2) * 100}%`,
                      width: '28px',
                      height: '28px',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    {state?.orderedMarkers ? index + 1 : null}
                    {/* {index + 1} */}
                  </div>
                );
              })}

            {imageLoaded &&
              markers.map((marker, index) => {
                if (draggingUid === marker.uid) return null;

                const markerResult = markerResultMap[marker.uid];
                const isCorrect = !!markerResult?.isCorrect;

                return (
                  <div
                    key={marker.uid}
                    onPointerDown={(e) => startMarkerDrag(e, marker.uid)}
                    className={`absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[4px] bg-secondary-05 text-xl font-bold text-white ${
                      answered
                        ? isCorrect
                          ? 'bg-primary border-2  border-success-04'
                          : 'bg-primary border-2  border-error-04'
                        : 'bg-primary cursor-grab border-warning-06 active:cursor-grabbing'
                    } ${
                      answered && state?.orderedMarkers
                        ? isCorrect
                          ? '!text-success-03'
                          : markerResult.labelUid && '!text-error-03'
                        : ''
                    } z-1`}
                    style={{
                      left: `${marker.x * 100}%`,
                      top: `${marker.y * 100}%`,
                    }}
                  >
                    {answered && state?.orderedMarkers
                      ? markerResult?.displayIndex || index + 1
                      : index + 1}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {!answered && dragPreview && (
        <div
          style={{
            position: 'fixed',
            left: dragPreview.x,
            top: dragPreview.y,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
          className={`flex h-8 w-8 items-center justify-center rounded-[4px] border-2 border-[#FDCA33] border-primary-04 bg-secondary-05 text-xl font-bold text-white shadow `}
        >
          {markers.findIndex((m) => m.uid === dragPreview.uid) + 1}
        </div>
      )}

      {(state.immediateFeedback || correctionType === 'autofeedback') &&
        courseStatus !== 'disabled' && (
          <PartialSubmitSection
            setSubmitted={setSubmitted}
            setModified={setModified}
            answered={answered}
            onSave={onSave.bind(null, false, false, state)}
          />
        )}

      {(resultPages || showAnswers || answered || submitted) &&
      state?.antwoord ? (
        <ExplanationSection state={state} />
      ) : null}
    </div>
  );
};

ShowOnPictureView.syncStates = (state, answer, correction) => {
  if (isEmpty(answer)) {
    answer.uid = state.uid;
    answer.markers = [];
    answer.submitted = false;
    answer.incomplete = true;
  }

  if (!Array.isArray(answer.markers)) {
    answer.markers = [];
  }

  const nextCorrection = buildCorrection(state, answer);

  correction.uid = nextCorrection.uid;
  correction.score = nextCorrection.score;
  correction.labels = nextCorrection.labels;
  correction.markers = nextCorrection.markers;
  correction.matchedCount = nextCorrection.matchedCount;
  correction.total = nextCorrection.total;
};
